// Hold-to-repeat state machine for click-and-hold UI controls
// (market spinners, etc.). Pure timing + injected timer/clock deps so
// the same logic powers the browser-side Pixi spinner AND the unit
// tests that exercise its behavior with vitest fake timers.
//
// Phase 7 Block A — playtest finding 9.3 (market spinner runaway).
// Originally the spinner's pointerdown handler called `onClick(1)`
// synchronously inside the event listener; that triggered a tab
// rebuild which destroyed the spinner Container, and the
// container-scoped `pointerup` cleanup never fired. The auto-repeat
// timer kept firing forever. Lifting the timing logic into a pure
// helper made the regression unit-testable; the renderSpinBtn caller
// now also installs a window-level pointerup so cleanup survives
// container destruction.

export type HoldRepeatOpts = {
  // Called once immediately on press, then on every auto-repeat tick.
  // step=1 for short holds, opts.bigStep once the big-step threshold
  // is crossed.
  readonly onTick: (step: number) => void;
  readonly delayMs: number;          // gap between press and first auto-repeat
  readonly intervalMs: number;       // auto-repeat cadence
  readonly bigStepAfterMs: number;   // hold duration at which step jumps
  readonly bigStep: number;          // step value once threshold crossed
  // Injectable timing deps. In the browser these are window.setTimeout
  // etc.; in tests, vitest fake-timer adapters.
  readonly setTimeout: (cb: () => void, ms: number) => unknown;
  readonly setInterval: (cb: () => void, ms: number) => unknown;
  readonly clearTimeout: (h: unknown) => void;
  readonly clearInterval: (h: unknown) => void;
  readonly now: () => number;
};

// Begins a hold-repeat session. Returns a `stop` function that the
// caller invokes from pointerup / pointercancel / pointerupoutside —
// idempotent so wiring it to multiple events is safe.
export function startHoldRepeat(opts: HoldRepeatOpts): () => void {
  const start = opts.now();
  // Immediate +1 so a quick click feels responsive even if the user
  // releases before the auto-repeat threshold.
  opts.onTick(1);

  let intervalHandle: unknown = null;
  const delayHandle = opts.setTimeout(() => {
    intervalHandle = opts.setInterval(() => {
      const held = opts.now() - start;
      const step = held >= opts.bigStepAfterMs ? opts.bigStep : 1;
      opts.onTick(step);
    }, opts.intervalMs);
  }, opts.delayMs);

  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    opts.clearTimeout(delayHandle);
    if (intervalHandle !== null) opts.clearInterval(intervalHandle);
  };
}
